/** Модалка полной информации об уведомлении.
 *
 *  Открывается по действию «Открыть» в карточке уведомления. Показывает
 *  заголовок/подзаголовок (локализованные по `source_code`), текст сообщения
 *  (если есть) и ПОЛНЫЙ набор данных из `payload` в виде списка «поле → значение».
 *  Внизу — те же inline-действия (Подтвердить / Отклонить / Готово), что и в
 *  карточке, кроме самого «Открыть».
 */
import { type Component, For, Show, createMemo, createSignal } from "solid-js";
import { Check, CheckCheck, Route, X, type LucideProps } from "lucide-solid";
import { ModalShell } from "~/components/ui/ModalShell";
import toast from "~/lib/toast";
import { apiRequest } from "~/lib/apiRequest";
import { useI18n } from "~/i18n";
import { useWorkspace } from "~/lib/workspace";
import { useNotifications } from "~/contexts/NotificationsProvider";
import { FormCruiseModal } from "~/pages/MonitoringPage/MessagesQueue/parts/FormCruiseModal";
import type { Notification, NotificationAction } from "~/lib/notifications";

interface Props {
  notification: Notification | null;
  onClose: () => void;
}

/** Ключи payload, которые не показываем в списке «Данные»: текст сообщения
 *  выводим отдельным блоком, а `*_code` дублируют человекочитаемые `*_name`.
 *  Любые технические ID (`*_id` / `id`) не показываем вообще — они не нужны
 *  оператору (фильтруются отдельно в `isHiddenKey`). */
const HIDDEN_KEYS = new Set([
  "preview",
  "text",
  "comment",
  "message_type_code",
  "device_type_code",
  "operation_type_code",
  "cargo_mode_code",
  "counterparty_role",
]);

/** Технические ID и явно скрытые ключи не показываем в «Данных». */
function isHiddenKey(key: string): boolean {
  if (HIDDEN_KEYS.has(key)) return true;
  return /(^id$|_id$)/i.test(key);
}

function prettifyKey(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\bid\b/i, "ID")
    .replace(/^\w/, (c) => c.toUpperCase());
}

interface ActionStyle {
  Icon: Component<LucideProps>;
  classes: string;
  labelKey: string;
  errorKey: string;
}

function actionStyle(code: string): ActionStyle | null {
  switch (code) {
    case "approve":
      return {
        Icon: Check,
        classes: "border-primary bg-primary-soft text-primary hover:bg-primary hover:text-white",
        labelKey: "notifications.actions.approve",
        errorKey: "notifications.errors.approveFail",
      };
    case "resolve":
      return {
        Icon: CheckCheck,
        classes: "border-primary bg-primary-soft text-primary hover:bg-primary hover:text-white",
        labelKey: "notifications.actions.resolve",
        errorKey: "notifications.errors.resolveFail",
      };
    case "reject":
      return {
        Icon: X,
        classes: "border-border bg-surface-3 text-danger hover:bg-danger-soft",
        labelKey: "notifications.actions.reject",
        errorKey: "notifications.errors.rejectFail",
      };
    default:
      // «open» и прочее в модалке не рисуем.
      return null;
  }
}

export const NotificationDetailModal: Component<Props> = (props) => {
  const { t, locale } = useI18n();
  const { markRead, refetch } = useNotifications();
  const { activeLocation } = useWorkspace();
  const [showForm, setShowForm] = createSignal(false);

  const notif = () => props.notification;
  const open = createMemo(() => notif() !== null && !showForm());

  // Заявка о прибытии (walk-up): даём тот же функционал, что в очереди
  // сообщений — «Сформировать рейс» (FormCruiseModal) вместо простого resolve.
  const isRegistration = createMemo(() => notif()?.source_code === "driver_registration");
  const formLocationId = createMemo<number | null>(
    () => notif()?.location_id ?? activeLocation()?.id ?? null,
  );
  const rejectAction = createMemo<NotificationAction | undefined>(
    () => (notif()?.actions ?? []).find((a) => a.code === "reject"),
  );

  const handleReject = async () => {
    const n = notif();
    const act = rejectAction();
    if (!n || !act) return;
    const reason = window.prompt(t("notifications.rejectReasonPrompt")) ?? "";
    try {
      await apiRequest(act.method, act.url.replace(/^\//, ""), { reason });
      void markRead(n.id);
      void refetch();
      props.onClose();
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      toast.error(`${t("notifications.errors.rejectFail")} · ${message}`);
    }
  };

  const payloadEntries = createMemo<Array<[string, string]>>(() => {
    const n = notif();
    if (!n) return [];
    const p = n.payload ?? {};
    const out: Array<[string, string]> = [];
    for (const [k, v] of Object.entries(p)) {
      if (isHiddenKey(k)) continue;
      if (v === null || v === undefined || v === "") continue;
      if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
        out.push([k, String(v)]);
      }
    }
    return out;
  });

  const messageText = createMemo<string>(() => {
    const p = notif()?.payload ?? {};
    const v = p.preview ?? p.text ?? p.comment;
    return typeof v === "string" ? v : "";
  });

  const params = createMemo<Record<string, string | number>>(() => {
    const p = notif()?.payload ?? {};
    const out: Record<string, string | number> = {};
    for (const [k, v] of Object.entries(p)) {
      if (typeof v === "string" || typeof v === "number") out[k] = v;
    }
    return out;
  });

  const fieldLabel = (key: string): string => {
    const localized = t(`notifications.fields.${key}`);
    return localized === `notifications.fields.${key}` ? prettifyKey(key) : localized;
  };

  const absoluteTime = (iso: string): string => {
    try {
      return new Intl.DateTimeFormat(locale() === "ru" ? "ru-RU" : "en-US", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  };

  const severityLabel = createMemo(() => {
    const s = notif()?.severity;
    if (!s) return "";
    const key = `notifications.severity.${s}`;
    const localized = t(key);
    return localized === key ? s : localized;
  });

  const nonOpenActions = createMemo(() =>
    (notif()?.actions ?? []).filter((a) => a.code !== "open" && actionStyle(a.code)),
  );

  const handleAction = async (act: NotificationAction) => {
    const n = notif();
    if (!n) return;
    try {
      await apiRequest(act.method, act.url.replace(/^\//, ""));
      void markRead(n.id);
      void refetch();
      props.onClose();
    } catch (e) {
      const style = actionStyle(act.code);
      const message = e instanceof Error ? e.message : String(e);
      toast.error(`${style ? t(style.errorKey) : ""} · ${message}`);
    }
  };

  return (
    <>
    <ModalShell
      open={open()}
      onClose={props.onClose}
      size="md"
      title={
        <Show when={notif()} fallback={""}>
          {t(`notifications.types.${notif()!.source_code}.title`, params())}
        </Show>
      }
    >
      <Show when={notif()}>
        {(n) => (
          <div class="flex flex-col gap-4">
            {/* Мета: подзаголовок + severity + время */}
            <div class="flex flex-wrap items-center gap-2 text-sm text-ink-muted">
              <span>{t(`notifications.types.${n().source_code}.subtitle`, params())}</span>
              <span class="text-ink-faint">·</span>
              <span>{absoluteTime(n().created_at)}</span>
              <Show when={severityLabel()}>
                <span
                  class="inline-flex items-center rounded-full px-2 py-0.5 text-2xs font-semibold"
                  classList={{
                    "bg-danger-soft text-danger-soft-ink": n().severity === "URGENT",
                    "bg-warning-soft text-warning-soft-ink": n().severity === "WARN",
                    "bg-info-soft text-info-soft-ink": n().severity === "INFO",
                  }}
                >
                  {severityLabel()}
                </span>
              </Show>
              <Show when={n().resolved_at}>
                <span class="inline-flex items-center rounded-full bg-success-soft px-2 py-0.5 text-2xs font-semibold text-success-soft-ink">
                  {t("notifications.resolvedBadge")}
                </span>
              </Show>
            </div>

            {/* Текст сообщения */}
            <Show when={messageText()}>
              <div class="rounded-lg border border-border-faint bg-surface-2 px-3 py-2.5">
                <div class="mb-1 text-2xs font-semibold uppercase tracking-wide text-ink-faint">
                  {t("notifications.detail.message")}
                </div>
                <div class="whitespace-pre-wrap break-words text-sm text-ink">
                  {messageText()}
                </div>
              </div>
            </Show>

            {/* Полный набор данных payload */}
            <div>
              <div class="mb-1.5 text-2xs font-semibold uppercase tracking-wide text-ink-faint">
                {t("notifications.detail.data")}
              </div>
              <Show
                when={payloadEntries().length > 0}
                fallback={<div class="text-sm text-ink-faint">{t("notifications.detail.noData")}</div>}
              >
                <dl class="divide-y divide-border-faint rounded-lg border border-border-faint">
                  <For each={payloadEntries()}>
                    {([key, value]) => (
                      <div class="flex items-start justify-between gap-4 px-3 py-2">
                        <dt class="text-sm text-ink-muted">{fieldLabel(key)}</dt>
                        <dd class="min-w-0 break-words text-right text-sm font-medium text-ink">
                          {value}
                        </dd>
                      </div>
                    )}
                  </For>
                </dl>
              </Show>
            </div>

            {/* Действия. Для заявки о прибытии (walk-up) — тот же функционал,
                что в очереди сообщений: «Отклонить» + «Сформировать рейс». */}
            <Show when={!n().resolved_at && isRegistration()}>
              <div class="flex flex-wrap justify-end gap-2 border-t border-border-faint pt-3">
                <Show when={rejectAction()}>
                  <button
                    type="button"
                    onClick={() => void handleReject()}
                    class="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-3 px-3 py-1.5 text-sm font-semibold text-danger transition-colors hover:bg-danger-soft"
                  >
                    <X size={14} stroke-width={2} />
                    <span>{t("notifications.actions.reject")}</span>
                  </button>
                </Show>
                <button
                  type="button"
                  disabled={formLocationId() === null}
                  onClick={() => setShowForm(true)}
                  class="inline-flex items-center gap-1.5 rounded-md border border-info bg-info px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-info/90 disabled:opacity-60"
                >
                  <Route size={14} stroke-width={2} />
                  <span>{t("notifications.actions.formCruise")}</span>
                </button>
              </div>
            </Show>

            {/* Действия для прочих типов (Подтвердить / Отклонить / Готово). */}
            <Show when={!n().resolved_at && !isRegistration() && nonOpenActions().length > 0}>
              <div class="flex flex-wrap justify-end gap-2 border-t border-border-faint pt-3">
                <For each={nonOpenActions()}>
                  {(act) => {
                    const styled = actionStyle(act.code)!;
                    return (
                      <button
                        type="button"
                        onClick={() => void handleAction(act)}
                        class={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-semibold transition-colors ${styled.classes}`}
                      >
                        <styled.Icon size={14} stroke-width={2} />
                        <span>{t(styled.labelKey)}</span>
                      </button>
                    );
                  }}
                </For>
              </div>
            </Show>
          </div>
        )}
      </Show>
    </ModalShell>

    {/* «Сформировать рейс» из уведомления — та же модалка, что в очереди
        сообщений. Открывается поверх (ModalShell выше в это время закрыт); на
        успех заявка резолвится на бэке, уведомление снимается (refetch),
        детальную модалку закрываем. */}
    <Show when={showForm() && notif() && formLocationId() !== null}>
      <FormCruiseModal
        open={true}
        messageId={notif()!.source_object_id}
        payload={notif()!.payload}
        locationId={formLocationId()!}
        onSuccess={() => {
          void markRead(notif()!.id);
          void refetch();
        }}
        onClose={() => {
          setShowForm(false);
          props.onClose();
        }}
      />
    </Show>
    </>
  );
};

export default NotificationDetailModal;
